import React, { useState, useEffect } from 'react';
import { Button } from './Button';

/**
 * EmptyState Component
 *
 * Premium empty states with beautiful illustrations, animations, and delightful copy.
 * Makes empty screens feel inviting and encouraging rather than broken.
 */

// Animated floating particles
function FloatingParticles({ color = 'coral' }) {
  const colors = {
    coral: ['bg-coral-200', 'bg-coral-300', 'bg-coral-100'],
    gold: ['bg-gold-200', 'bg-gold-300', 'bg-gold-100'],
    emerald: ['bg-emerald-200', 'bg-emerald-300', 'bg-emerald-100'],
  };

  const particles = colors[color] || colors.coral;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className={`
            absolute rounded-full opacity-60
            ${particles[i % 3]}
            animate-float
          `}
          style={{
            width: `${8 + Math.random() * 12}px`,
            height: `${8 + Math.random() * 12}px`,
            left: `${10 + Math.random() * 80}%`,
            top: `${10 + Math.random() * 80}%`,
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${3 + Math.random() * 2}s`,
          }}
        />
      ))}
    </div>
  );
}

// SVG Illustration Components
function GiftBoxIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-20 h-20">
      <defs>
        <linearGradient id="giftGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F43F5E" />
          <stop offset="100%" stopColor="#FB7185" />
        </linearGradient>
        <linearGradient id="ribbonGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>
      </defs>
      {/* Box body */}
      <rect x="20" y="50" width="80" height="55" rx="8" fill="url(#giftGrad)" className="animate-pulse-subtle" />
      {/* Box lid */}
      <rect x="15" y="40" width="90" height="15" rx="4" fill="url(#giftGrad)" />
      {/* Vertical ribbon */}
      <rect x="52" y="40" width="16" height="65" fill="url(#ribbonGrad)" />
      {/* Horizontal ribbon */}
      <rect x="20" y="68" width="80" height="12" fill="url(#ribbonGrad)" />
      {/* Bow center */}
      <circle cx="60" cy="35" r="8" fill="url(#ribbonGrad)" />
      {/* Bow loops */}
      <ellipse cx="45" cy="30" rx="12" ry="8" fill="url(#ribbonGrad)" transform="rotate(-20 45 30)" />
      <ellipse cx="75" cy="30" rx="12" ry="8" fill="url(#ribbonGrad)" transform="rotate(20 75 30)" />
      {/* Sparkles */}
      <circle cx="95" cy="25" r="3" fill="#FBBF24" className="animate-pulse" />
      <circle cx="25" cy="35" r="2" fill="#FBBF24" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
      <circle cx="100" cy="55" r="2" fill="#FBBF24" className="animate-pulse" style={{ animationDelay: '1s' }} />
    </svg>
  );
}

function HeartIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <defs>
        <linearGradient id="heartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F43F5E" />
          <stop offset="100%" stopColor="#EC4899" />
        </linearGradient>
      </defs>
      {/* Main heart */}
      <path
        d="M60 100 C20 65 20 30 45 25 C60 22 60 40 60 40 C60 40 60 22 75 25 C100 30 100 65 60 100Z"
        fill="url(#heartGrad)"
        className="animate-pulse-subtle"
      />
      {/* Small hearts */}
      <path d="M25 20 C20 15 20 10 25 10 C28 10 28 15 28 15 C28 15 28 10 31 10 C36 10 36 15 25 20Z" fill="#FDA4AF" className="animate-float" style={{ animationDelay: '0.3s' }} />
      <path d="M90 15 C85 10 85 5 90 5 C93 5 93 10 93 10 C93 10 93 5 96 5 C101 5 101 10 90 15Z" fill="#FDA4AF" className="animate-float" style={{ animationDelay: '0.6s' }} />
      {/* Sparkles */}
      <circle cx="15" cy="45" r="3" fill="#FBBF24" className="animate-pulse" />
      <circle cx="105" cy="40" r="2" fill="#FBBF24" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
    </svg>
  );
}

function SparkleIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <defs>
        <linearGradient id="sparkleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#F59E0B" />
        </linearGradient>
      </defs>
      {/* Main star */}
      <path
        d="M60 10 L68 45 L105 50 L72 65 L80 105 L60 75 L40 105 L48 65 L15 50 L52 45 Z"
        fill="url(#sparkleGrad)"
        className="animate-pulse-subtle"
      />
      {/* Small stars */}
      <path d="M20 25 L23 32 L30 33 L24 37 L26 45 L20 40 L14 45 L16 37 L10 33 L17 32 Z" fill="#FDE68A" className="animate-float" style={{ animationDelay: '0.2s' }} />
      <path d="M95 70 L97 75 L102 76 L98 79 L99 85 L95 81 L91 85 L92 79 L88 76 L93 75 Z" fill="#FDE68A" className="animate-float" style={{ animationDelay: '0.8s' }} />
      {/* Sparkle dots */}
      <circle cx="35" cy="80" r="3" fill="#FBBF24" className="animate-pulse" />
      <circle cx="85" cy="25" r="2" fill="#FBBF24" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
    </svg>
  );
}

function SearchIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <defs>
        <linearGradient id="searchGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#64748B" />
          <stop offset="100%" stopColor="#94A3B8" />
        </linearGradient>
      </defs>
      {/* Magnifying glass circle */}
      <circle cx="50" cy="50" r="30" fill="none" stroke="url(#searchGrad)" strokeWidth="8" />
      {/* Glass shine */}
      <path d="M35 35 Q45 25 55 35" fill="none" stroke="#CBD5E1" strokeWidth="3" strokeLinecap="round" />
      {/* Handle */}
      <line x1="72" y1="72" x2="100" y2="100" stroke="url(#searchGrad)" strokeWidth="10" strokeLinecap="round" />
      {/* Question mark */}
      <text x="42" y="58" fontSize="24" fill="#94A3B8" fontFamily="sans-serif" fontWeight="bold">?</text>
    </svg>
  );
}

function CalendarIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <defs>
        <linearGradient id="calGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#34D399" />
        </linearGradient>
      </defs>
      {/* Calendar body */}
      <rect x="15" y="30" width="90" height="75" rx="8" fill="white" stroke="#E2E8F0" strokeWidth="2" />
      {/* Calendar header */}
      <rect x="15" y="30" width="90" height="20" rx="8" fill="url(#calGrad)" />
      {/* Calendar holes */}
      <circle cx="35" cy="25" r="4" fill="#94A3B8" />
      <circle cx="85" cy="25" r="4" fill="#94A3B8" />
      {/* Check mark */}
      <path d="M45 75 L55 85 L80 55" fill="none" stroke="url(#calGrad)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" className="animate-pulse-subtle" />
      {/* Sparkles */}
      <circle cx="100" cy="40" r="3" fill="#FBBF24" className="animate-pulse" />
      <circle cx="20" cy="80" r="2" fill="#FBBF24" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
    </svg>
  );
}

function ErrorIllustration() {
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <defs>
        <linearGradient id="errorGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F43F5E" />
          <stop offset="100%" stopColor="#FB7185" />
        </linearGradient>
      </defs>
      {/* Cloud */}
      <ellipse cx="60" cy="55" rx="35" ry="25" fill="#F1F5F9" />
      <ellipse cx="40" cy="50" rx="20" ry="15" fill="#F1F5F9" />
      <ellipse cx="80" cy="50" rx="20" ry="15" fill="#F1F5F9" />
      {/* Sad face */}
      <circle cx="48" cy="50" r="3" fill="#64748B" />
      <circle cx="72" cy="50" r="3" fill="#64748B" />
      <path d="M50 65 Q60 58 70 65" fill="none" stroke="#64748B" strokeWidth="3" strokeLinecap="round" />
      {/* Rain drops */}
      <ellipse cx="45" cy="85" rx="3" ry="5" fill="url(#errorGrad)" className="animate-float" style={{ animationDelay: '0s' }} />
      <ellipse cx="60" cy="90" rx="3" ry="5" fill="url(#errorGrad)" className="animate-float" style={{ animationDelay: '0.3s' }} />
      <ellipse cx="75" cy="85" rx="3" ry="5" fill="url(#errorGrad)" className="animate-float" style={{ animationDelay: '0.6s' }} />
    </svg>
  );
}

// Illustration map
const illustrations = {
  gift: GiftBoxIllustration,
  heart: HeartIllustration,
  sparkle: SparkleIllustration,
  search: SearchIllustration,
  calendar: CalendarIllustration,
  error: ErrorIllustration,
};

export function EmptyState({
  icon,
  illustration,
  illustrationType, // 'gift' | 'heart' | 'sparkle' | 'search' | 'calendar' | 'error'
  title,
  description,
  action,
  actionLabel,
  onAction,
  secondaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  particleColor = 'coral',
  className = '',
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const IllustrationComponent = illustrationType ? illustrations[illustrationType] : null;

  return (
    <div
      className={`
        relative empty-state py-8 px-6
        ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        transition-all duration-500 ease-out
        ${className}
      `}
    >
      {/* Floating particles */}
      <FloatingParticles color={particleColor} />

      {/* Content */}
      <div className="relative z-10">
        {/* Illustration or Icon */}
        {IllustrationComponent ? (
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-coral-100 to-coral-50 rounded-full blur-xl scale-125 opacity-50" />
              <IllustrationComponent />
            </div>
          </div>
        ) : illustration ? (
          <div className="text-5xl mb-4 animate-float relative">
            <div className="absolute inset-0 bg-gradient-to-br from-coral-100 to-coral-50 rounded-full blur-xl scale-125 opacity-50" />
            <span className="relative">{illustration}</span>
          </div>
        ) : icon ? (
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-coral-100 to-coral-50 flex items-center justify-center text-coral-500 mb-4 shadow-lg animate-float">
            {icon}
          </div>
        ) : null}

        {/* Title */}
        {title && (
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            {title}
          </h3>
        )}

        {/* Description */}
        {description && (
          <p className="text-slate-500 text-base max-w-sm mx-auto leading-relaxed">
            {description}
          </p>
        )}

        {/* Actions */}
        {(action || actionLabel) && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {action || (
              <button
                onClick={onAction}
                className="
                  px-6 py-3 rounded-xl
                  bg-gradient-to-r from-coral-500 to-coral-600
                  text-white font-semibold text-base
                  shadow-lg shadow-coral-500/30
                  hover:shadow-xl hover:shadow-coral-500/40 hover:scale-105
                  active:scale-95
                  transition-all duration-300
                "
              >
                {actionLabel}
              </button>
            )}

            {(secondaryAction || secondaryActionLabel) && (
              secondaryAction || (
                <button
                  onClick={onSecondaryAction}
                  className="
                    px-6 py-3 rounded-xl
                    text-slate-600 font-medium
                    hover:bg-slate-100
                    transition-all duration-200
                  "
                >
                  {secondaryActionLabel}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pre-built empty states for common scenarios
 */

export function EmptyPeople({ onAdd }) {
  return (
    <EmptyState
      illustrationType="heart"
      title="Your gift circle is empty"
      description="Add the people you care about and never forget an important occasion again."
      actionLabel="Add your first person"
      onAction={onAdd}
      particleColor="coral"
    />
  );
}

export function EmptyGifts({ personName, onAdd }) {
  return (
    <EmptyState
      illustrationType="gift"
      title="No gifts recorded yet"
      description={`When you give ${personName || 'them'} something special, record it here so you never repeat yourself.`}
      actionLabel="Add a gift"
      onAction={onAdd}
      particleColor="gold"
    />
  );
}

export function EmptyUpcoming() {
  return (
    <EmptyState
      illustrationType="calendar"
      title="You're all caught up!"
      description="No occasions in the next 30 days. Enjoy the peace and browse gift ideas!"
      secondaryActionLabel="Browse gift ideas anyway"
      particleColor="emerald"
    />
  );
}

export function EmptySearch({ query, onClear }) {
  return (
    <EmptyState
      illustrationType="search"
      title="No results found"
      description={`We couldn't find anything matching "${query}". Try a different search term.`}
      actionLabel="Clear search"
      onAction={onClear}
      particleColor="coral"
    />
  );
}

export function EmptyTimeline({ onAdd }) {
  return (
    <EmptyState
      illustrationType="gift"
      title="Your gift-giving journey starts here"
      description="Add your first person and we'll help you never forget an important occasion again."
      actionLabel="Add someone special"
      onAction={onAdd}
      particleColor="coral"
    />
  );
}

export function ErrorState({ title, description, onRetry }) {
  return (
    <EmptyState
      illustrationType="error"
      title={title || "Something went wrong"}
      description={description || "We're having trouble loading this. Please try again."}
      actionLabel="Try again"
      onAction={onRetry}
      particleColor="coral"
    />
  );
}

export default EmptyState;
